import { useEffect, useMemo, useState } from 'react';
import { builder, BuilderContent } from '@builder.io/sdk';
import { ArticlePreview, BuilderArticlePageReference } from '@/types/article';

const ARTICLE_FIELDS =
  'data.title,data.url,data.heroImage,data.category,data.tags,data.featured,lastUpdated,firstPublished';

function initBuilderSdk(): boolean {
  if (!process.env.NEXT_PUBLIC_BUILDER_API_KEY) return false;
  builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY);
  return true;
}

function mapBuilderResults(results: BuilderContent[]): ArticlePreview[] {
  return results
    .filter((article) => article.data)
    .map((article) => ({
      ...article.data,
      lastUpdated: article.lastUpdated,
      firstPublished: article.firstPublished,
    })) as ArticlePreview[];
}

function hasCategoryData(article: ArticlePreview): boolean {
  return !!(article.category?.value && (article.category.value as BuilderContent).data);
}

let categoryCache = new Map<string, BuilderContent>();

interface UseBuilderArticlesOptions {
  autoSelectLatest: boolean;
  limit: number;
  latestArticles: ArticlePreview[] | undefined;
  articleInputs: { article?: BuilderArticlePageReference }[];
  categories?: BuilderContent[];
}

/**
 * Resolves articles for Builder components that support both auto-select and
 * manual modes. Handles two Builder SDK editor limitations:
 *
 * 1. Server-fetched state data (featuredArticles) isn't preserved in the
 *    editor's client-side state, so auto-select mode needs a client-side
 *    fallback fetch.
 * 2. Nested references (e.g. category within article) aren't resolved in
 *    the editor's content state, so manual articles need client-side
 *    enrichment to display category tags.
 */
export function useBuilderArticles({
  autoSelectLatest,
  limit,
  latestArticles,
  articleInputs,
  categories = [],
}: UseBuilderArticlesOptions): ArticlePreview[] {
  const articleModel = articleInputs[0]?.article?.model ?? 'article';

  // --- Category resolution (server data → module cache → client fetch) ---
  const serverCategoryMap = useMemo(() => {
    if (categories.length > 0) {
      const map = new Map<string, BuilderContent>(categories.filter((c) => c.id).map((c) => [c.id!, c]));
      categoryCache = map;
      return map;
    }
    return categoryCache;
  }, [categories]);

  // Client-side category fetch — same simple pattern as the auto-select
  // fallback below, which is proven to work in the Builder editor.
  const [clientCategories, setClientCategories] = useState<Map<string, BuilderContent>>(() => categoryCache);

  useEffect(() => {
    if (serverCategoryMap.size > 0 || clientCategories.size > 0) return;
    if (!initBuilderSdk()) return;

    builder
      .getAll('category', {
        limit: 100,
        fields: 'data,id,name',
        options: { noTargeting: true },
      })
      .then((results) => {
        const map = new Map<string, BuilderContent>(results.filter((c) => c.id).map((c) => [c.id!, c]));
        categoryCache = map;
        setClientCategories(map);
      })
      .catch(() => {});
  }, [serverCategoryMap.size, clientCategories.size]);

  const categoryMap = useMemo(
    () => (serverCategoryMap.size > 0 ? serverCategoryMap : clientCategories),
    [serverCategoryMap, clientCategories]
  );

  // --- Auto-select fallback ---
  const [fallbackArticles, setFallbackArticles] = useState<ArticlePreview[]>([]);

  useEffect(() => {
    if (!autoSelectLatest || (latestArticles && latestArticles.length > 0) || fallbackArticles.length > 0) return;
    if (!initBuilderSdk()) return;

    builder
      .getAll(articleModel, {
        fields: ARTICLE_FIELDS,
        limit,
        sort: { firstPublished: -1 },
        options: { noTargeting: true, includeRefs: true },
      })
      .then((results) => setFallbackArticles(mapBuilderResults(results)));
  }, [autoSelectLatest, latestArticles, limit, fallbackArticles.length, articleModel]);

  // --- Manual article mapping with synchronous category resolution ---
  const mappedArticles = useMemo(() => {
    const articles = articleInputs.map((a) => a.article?.value?.data).filter((data) => !!data) as ArticlePreview[];

    if (!categoryMap.size) return articles;

    return articles.map((article) => {
      const catId = article.category?.id;
      if (catId && !hasCategoryData(article) && categoryMap.has(catId)) {
        return {
          ...article,
          category: { ...article.category!, value: categoryMap.get(catId)! },
        };
      }
      return article;
    }) as ArticlePreview[];
  }, [articleInputs, categoryMap]);

  // --- Resolve final article list ---
  if (autoSelectLatest) {
    const resolved = latestArticles?.length ? latestArticles : fallbackArticles;
    return resolved.slice(0, limit);
  }

  return mappedArticles;
}
